// src/pages/GlobalLocations.js
import React, { useEffect, useCallback, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import ScrollReveal from '../components/ScrollReveal';
import { Link } from 'react-router-dom';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Premium teardrop pin marker with company logo inset
const logoIcon = new L.divIcon({
  className: '',
  html: `
    <div style="
      position:relative;
      width:48px;
      height:64px;
      display:flex;
      flex-direction:column;
      align-items:center;
      filter:drop-shadow(0 8px 16px rgba(0,0,0,0.32)) drop-shadow(0 2px 4px rgba(0,0,0,0.20));
    ">
      <!-- Pin head: navy circle with orange accent ring -->
      <div style="
        width:10px;
        height:10px;
        background:linear-gradient(150deg, #ffffff 0%, #ffffff 60%, #ffffff 100%);
        border-radius:50%;
        border:2.5px solid #ffeb35;
        box-shadow:
          inset 0 1px 4px rgba(255,255,255,0.10),
          inset 0 -1px 3px rgba(0,0,0,0.30),
          0 0 0 1px rgba(255,107,53,0.20);
        display:flex;
        align-items:center;
        justify-content:center;
        flex-shrink:0;
        position:relative;
        z-index:1;
      ">
        <!-- Specular highlight arc -->
        <div style="
          position:absolute;
          top:5px;
          left:8px;
          width:18px;
          height:8px;
          background:rgba(255,255,255,0.10);
          border-radius:50%;
          transform:rotate(-20deg);
          pointer-events:none;
        "></div>
        <!-- Inner white disc with logo -->
        <div style="
          width:33px;
          height:33px;
          border-radius:50%;
          background:#ffffff;
          display:flex;
          align-items:center;
          justify-content:center;
          overflow:hidden;
          box-shadow:inset 0 1px 3px rgba(0,0,0,0.12);
          flex-shrink:0;
        ">
          <img
            src='/assets/opt/company-logo.webp'
            style='width:26px;height:26px;object-fit:contain;display:block;'
          />
        </div>
      </div>

      <!-- Pin tail: two-tone triangle for depth -->
      <div style="
        position:relative;
        margin-top:-2px;
        flex-shrink:0;
      ">
        <!-- Main triangle -->
        <div style="
          width:0;height:0;
          border-left:10px solid transparent;
          border-right:10px solid transparent;
          border-top:18px solid #0c0c0c;
        "></div>
        <!-- Orange edge highlight on left face -->
        <div style="
          position:absolute;
          top:0;left:0;
          width:0;height:0;
          border-left:5px solid transparent;
          border-right:0px solid transparent;
          border-top:10px solid rgba(255,107,53,0.22);
        "></div>
      </div>

      <!-- Ground shadow ellipse -->
      <div style="
        position:absolute;
        bottom:-1px;
        left:50%;
        transform:translateX(-50%);
        width:18px;
        height:5px;
        background:rgba(0,0,0,0.20);
        border-radius:50%;
        filter:blur(3px);
      "></div>
    </div>
  `,
  iconSize: [48, 64],
  iconAnchor: [24, 64],
  popupAnchor: [0, -70],
});

// Controller that exposes the map instance
const MapController = ({ onMapReady }) => {
  const map = useMap();
  useEffect(() => {
    if (onMapReady) onMapReady(map);
  }, [map, onMapReady]);
  return null;
};

const GlobalLocations = () => {
  const mapRef = React.useRef(null);
  const [isGlobalView, setIsGlobalView] = useState(false);

  // Location data (unchanged)
  const locations = [
    { id: 1, country: "United States", city: "New York", address: "AALI GROUP Americas HQ, 350 Fifth Avenue, Suite 5500, New York, NY 10118", lat: 40.7128, lng: -74.0060, phone: "+1 (212) 555-0123", email: "info@aaligroup.co", type: "International Office", flag: "🇺🇸" },
    { id: 2, country: "United Arab Emirates", city: "Dubai", address: "AALI GROUP Middle East, Burj Khalifa, 123rd Floor, Downtown Dubai, UAE", lat: 25.2048, lng: 55.2708, phone: "+971 4 123 4567", email: "info@aaligroup.co", type: "Regional HQ - Middle East", flag: "🇦🇪" },
    { id: 3, country: "Singapore", city: "Singapore", address: "AALI GROUP Asia Pacific, Marina Bay Financial Centre, Tower 3, Singapore 018982", lat: 1.3521, lng: 103.8198, phone: "+65 6789 0123", email: "info@aaligroup.co", type: "Regional HQ - Asia Pacific", flag: "🇸🇬" },
    { id: 4, country: "Malaysia", city: "Kuala Lumpur", address: "AALI GROUP Malaysia, Petronas Twin Towers, Level 45, Kuala Lumpur 50088", lat: 3.1390, lng: 101.6869, phone: "+60 3 1234 5678", email: "info@aaligroup.co", type: "ASEAN Operations", flag: "🇲🇾" },
    { id: 5, country: "India", city: "New Delhi", address: "AALI GROUP India - North Region, Connaught Place, New Delhi - 110001", lat: 28.7041, lng: 77.1025, phone: "+91 11 4567 8901", email: "info@aaligroup.co", type: "Operations - North India", flag: "🇮🇳" },
    { id: 6, country: "India", city: "Mumbai", address: "AALI GROUP India - West Region, Bandra Kurla Complex, Mumbai - 400051", lat: 19.0760, lng: 72.8777, phone: "+91 22 6789 0123", email: "info@aaligroup.co", type: "Operations - West India", flag: "🇮🇳" },
    { id: 7, country: "India", city: "Bangalore", address: "AALI GROUP India - South Region, UB City, Lavelle Road, Bangalore - 560001", lat: 12.9716, lng: 77.5946, phone: "+91 80 4567 8901", email: "info@aaligroup.co", type: "Operations - South India", flag: "🇮🇳" },
    { id: 8, country: "India", city: "Chennai", address: "AALI GROUP India - Tamil Nadu, Express Avenue, Royapettah, Chennai - 600014", lat: 13.0827, lng: 80.2707, phone: "+91 44 6789 0123", email: "info@aaligroup.co", type: "Operations - East Coast", flag: "🇮🇳" },
    { id: 9, country: "India", city: "Hyderabad", address: "AALI GROUP India - Telangana, HITEC City, Hyderabad - 500081", lat: 17.4468627, lng: 78.3862683, phone: "+91 83 4108 5415", email: "info@aaligroup.co", type: "Head Quarters - Telangana", flag: "🇮🇳" },
    { id: 10, country: "India", city: "Guntur", address: "AALI GROUP India - Syamala Nagar, Guntur, Andhra Pradesh, 522006", lat: 16.3071210, lng: 80.4097426, phone: "+91 83 4108 5415", email: "info@aaligroup.co", type: "Operational Office - Andhra Pradesh", flag: "🇮🇳" },
  ];

  const allMarkersData = locations.map(loc => [loc.lat, loc.lng]);
  const indiaRegionMarkers = locations.filter(loc => loc.id !== 1).map(loc => [loc.lat, loc.lng]);

  // Called once when map is ready: set initial India view
  const handleMapReady = useCallback((map) => {
    mapRef.current = map;
    const bounds = L.latLngBounds(indiaRegionMarkers);
    map.fitBounds(bounds, {
      paddingTopLeft: [80, 60],
      paddingBottomRight: [40, 60],
      maxZoom: 5,
    });
  }, []); // eslint-disable-line

  // Toggle between global view and India view
  const toggleView = useCallback(() => {
    if (!mapRef.current) return;

    if (isGlobalView) {
      // Go back to India region view (same as initial)
      const bounds = L.latLngBounds(indiaRegionMarkers);
      mapRef.current.flyToBounds(bounds, {
        paddingTopLeft: [80, 60],
        paddingBottomRight: [40, 60],
        maxZoom: 5,
        duration: 1.2,
      });
      setIsGlobalView(false);
    } else {
      // Show all global offices
      const bounds = L.latLngBounds(allMarkersData);
      mapRef.current.flyToBounds(bounds, {
        padding: [60, 60],
        maxZoom: 4,
        duration: 1.2,
      });
      setIsGlobalView(true);
    }
  }, [isGlobalView]); // eslint-disable-line

  // Scroll reveal effect
  useEffect(() => {
    const handleScrollReveal = () => {
      document.querySelectorAll('.reveal-on-scroll').forEach(el => {
        if (el.getBoundingClientRect().top < window.innerHeight - 100) {
          el.classList.add('revealed');
        }
      });
    };
    window.addEventListener('scroll', handleScrollReveal);
    handleScrollReveal();
    return () => window.removeEventListener('scroll', handleScrollReveal);
  }, []);

  return (
    <>
      {/* Hero Section (unchanged) */}
      <section className="global-hero">
        <div className="container">
          <div className="hero-content-center">
            <span className="hero-badge">🌍 Global Presence</span>
            <h1 className="hero-title-main">Our Worldwide Footprint</h1>
            <p className="hero-subtitle-main">
              Strategically located across 6 countries and 10+ major cities, serving clients globally with excellence.
            </p>
            <div className="hero-stats">
              <div className="stat-item"><span className="stat-number">6</span><span className="stat-label">Countries</span></div>
              <div className="stat-item"><span className="stat-number">10+</span><span className="stat-label">Offices</span></div>
              <div className="stat-item"><span className="stat-number">4</span><span className="stat-label">Continents</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section with Toggle Button */}
      <section className="map-section reveal-on-scroll">
        <div className="container">
          <div className="section-header-center">
            <span className="section-tag">Interactive Map</span>
            <h2>Explore Our Locations</h2>
            <p>Click on any marker to view detailed office information</p>
          </div>

          {/* Toggle button - text and style change based on isGlobalView */}
          <div className="map-toolbar">
            <button
              className={`fit-all-btn ${isGlobalView ? 'active' : ''}`}
              onClick={toggleView}
            >
              {isGlobalView ? '🇮🇳 Back to India Region' : '🌍 View All Global Offices'}
            </button>
          </div>

          <div className="map-wrapper">
            <MapContainer
              center={[20, 80]}
              zoom={4}
              minZoom={2}
              maxBounds={[[-85, -180], [85, 180]]}
              maxBoundsViscosity={1.0}
              worldCopyJump={false}
              style={{ height: '550px', width: '100%', borderRadius: '24px' }}
              zoomControl={true}
              scrollWheelZoom={true}
            >
              {/* Esri light-grey basemap + labels: no API key needed (CARTO basemaps now require one) */}
              <TileLayer
                attribution='Tiles &copy; Esri &mdash; Esri, HERE, Garmin, &copy; OpenStreetMap contributors'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                maxNativeZoom={16}
                noWrap={true}
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
                maxNativeZoom={16}
                noWrap={true}
              />

              {locations.map((loc) => (
                <Marker key={loc.id} position={[loc.lat, loc.lng]} icon={logoIcon}>
                  <Popup>
                    <div className="popup-content">
                      <h4>{loc.flag} {loc.city}, {loc.country}</h4>
                      <p className="popup-type">{loc.type}</p>
                      <p className="popup-address">{loc.address}</p>
                      <p className="popup-contact">📞 {loc.phone}</p>
                      <p className="popup-contact">✉️ {loc.email}</p>
                    </div>
                  </Popup>
                </Marker>
              ))}

              <MapController onMapReady={handleMapReady} />
            </MapContainer>
          </div>
        </div>
      </section>

      {/* CTA Section (unchanged) */}
      <div className="global-cta reveal-on-scroll">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Collaborate?</h2>
            <p>Visit any of our global offices or connect with our team for business inquiries.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn-primary">Contact Global Team</Link>
              <Link to="/careers" className="btn-outline-light">Explore Careers Worldwide</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GlobalLocations;